import React, { useState, useEffect, useRef } from 'react';
import { Brain, Sparkles, X, MessageSquare, BarChart3, Send } from 'lucide-react';

function LiveAssessmentModal({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [scores, setScores] = useState({ analytical: 0, resilience: 0, adaptability: 0 });
  const [isTyping, setIsTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);

  const sequence = [
    { sender: 'ai', text: "Hello. Let's map your developmental potential. To start, how do you typically react when a task feels overwhelming?", delay: 1000 },
    { sender: 'user', text: "I try to step back and break it down into smaller, manageable chunks.", delay: 3000 },
    { sender: 'system', text: "Analyzing cognitive approach...", delay: 4500, newScores: { analytical: 82, resilience: 60, adaptability: 50 } },
    { sender: 'ai', text: "Excellent self-awareness. That demonstrates strong executive functioning. How easily do you adapt if that structured plan gets derailed?", delay: 6500 },
    { sender: 'user', text: "That frustrates me initially, but I usually figure out a workaround.", delay: 9000 },
    { sender: 'system', text: "Recalibrating trait metrics...", delay: 10500, newScores: { analytical: 85, resilience: 72, adaptability: 78 } },
    { sender: 'ai', text: "It's normal to feel frustration. The ability to find a workaround shows high cognitive flexibility. Our report will provide strategies to reduce that initial friction.", delay: 12500 }
  ];

  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setScores({ analytical: 0, resilience: 0, adaptability: 0 });
      let currentIdx = 0;

      const processSequence = () => {
        if (currentIdx >= sequence.length) {
          setIsTyping(false);
          return;
        }
        const step = sequence[currentIdx];
        if (step.sender === 'ai' || step.sender === 'system') setIsTyping(true);
        else setIsTyping(false);

        setTimeout(() => {
          setIsTyping(false);
          setMessages(prev => [...prev, step]);
          if (step.newScores) setScores(step.newScores);
          currentIdx++;
          processSequence();
        }, step.delay - (currentIdx === 0 ? 0 : sequence[currentIdx - 1].delay));
      };

      processSequence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSendMessage = (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;

    setDraft('');
    setMessages(prev => [...prev, { sender: 'user', text }]);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        {
          sender: 'ai',
          text: 'Thank you. MindVrik will use answers like this to map aptitude, learning style, emotional patterns, and career-fit indicators in the final advisory report.',
        },
      ]);
      setScores(prev => ({
        analytical: Math.max(prev.analytical, 84),
        resilience: Math.max(prev.resilience, 74),
        adaptability: Math.max(prev.adaptability, 80),
      }));
    }, 900);
  };

  if (!isOpen) return null;

  return (
    <div
      className="assessment-modal-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        background: 'rgba(26,32,44,0.65)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        .modal-bounce { animation: bounce 1s infinite; }
        @keyframes bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
      `}</style>

      <div className="assessment-modal-panel" style={{
        background: '#fff',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '900px',
        height: '85vh',
        maxHeight: '600px',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        boxShadow: '0 20px 70px rgba(0,0,0,0.22)',
        position: 'relative',
      }}>
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '16px', right: '16px', zIndex: 10,
            width: '36px', height: '36px', borderRadius: '50%',
            background: '#f3f4f6', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#6b7280',
          }}
        >
          <X size={18} />
        </button>

        {/* Left: Chat */}
        <div className="assessment-chat-pane" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#FAFAF8', borderRight: '1px solid #f0f0ee' }}>
          {/* Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0ee', background: '#fff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #2A9D8F, #4FC3F7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(42,157,143,0.3)',
            }}>
              <Brain size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#1A202C', fontFamily: 'Manrope, sans-serif' }}>MindVrik AI Assessor</div>
              <div style={{ fontSize: '11px', color: '#2A9D8F', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'Manrope, sans-serif' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2A9D8F', display: 'inline-block', animation: 'pulse 2s infinite' }}></span>
                Session Active
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                {msg.sender === 'system' ? (
                  <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#2A9D8F', fontSize: '11px', fontWeight: 500, opacity: 0.75, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Manrope, sans-serif' }}>
                    <Sparkles size={13} />
                    <span>{msg.text}</span>
                    <Sparkles size={13} />
                  </div>
                ) : (
                  <div style={{
                    maxWidth: '80%',
                    padding: '14px 16px',
                    borderRadius: msg.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    fontFamily: 'Manrope, sans-serif',
                    background: msg.sender === 'user' ? '#2A9D8F' : '#fff',
                    color: msg.sender === 'user' ? '#fff' : '#1A202C',
                    border: msg.sender === 'user' ? 'none' : '1px solid #f0f0ee',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                    {msg.text}
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ background: '#fff', border: '1px solid #f0f0ee', borderRadius: '18px 18px 18px 4px', padding: '14px 18px', display: 'flex', gap: '5px', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  {[0, 150, 300].map((d, i) => (
                    <span key={i} className="modal-bounce" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#9ca3af', display: 'inline-block', animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ padding: '16px', background: '#fff', borderTop: '1px solid #f0f0ee' }}>
            <form onSubmit={handleSendMessage} style={{ background: '#FAFAF8', border: '1px solid #e5e7eb', borderRadius: '999px', padding: '8px 10px 8px 18px', fontSize: '13px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Manrope, sans-serif' }}>
              <MessageSquare size={15} />
              <input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Type your response..."
                aria-label="Type your assessment response"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: '#1A202C',
                  fontSize: '13px',
                  fontFamily: 'Manrope, sans-serif',
                }}
              />
              <button
                type="submit"
                aria-label="Send response"
                disabled={!draft.trim()}
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  border: 'none',
                  background: draft.trim() ? '#2A9D8F' : '#d1d5db',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: draft.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
              >
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>

        {/* Right: Scores */}
        <div className="assessment-score-pane" style={{ width: '300px', background: '#fff', padding: '32px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '240px', height: '240px', background: 'rgba(79,195,247,0.08)', borderRadius: '50%', filter: 'blur(50px)' }} />

          <div style={{ marginBottom: '28px', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2A9D8F', marginBottom: '6px' }}>
              <BarChart3 size={19} />
              <span style={{ fontWeight: 700, fontSize: '16px', fontFamily: 'Manrope, sans-serif', color: '#1A202C' }}>Live Trait Mapping</span>
            </div>
            <p style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'Manrope, sans-serif' }}>Scores adjust dynamically based on conversation heuristics.</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', position: 'relative' }}>
            {[
              { label: 'Analytical Alignment', key: 'analytical', color: 'linear-gradient(90deg, #2A9D8F, #4FC3F7)' },
              { label: 'Emotional Resilience', key: 'resilience', color: '#7A9E7E' },
              { label: 'Cognitive Flexibility', key: 'adaptability', color: '#1A202C' },
            ].map(({ label, key, color }) => (
              <div key={key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#1A202C', fontFamily: 'Manrope, sans-serif' }}>{label}</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#2A9D8F', fontFamily: 'Manrope, sans-serif' }}>{scores[key]}%</span>
                </div>
                <div style={{ width: '100%', height: '8px', background: '#f3f4f6', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    borderRadius: '999px',
                    background: color,
                    width: `${scores[key]}%`,
                    transition: 'width 1s ease-out',
                  }} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '36px', background: '#FAFAF8', border: '1px solid #f0f0ee', borderRadius: '16px', padding: '16px', position: 'relative' }}>
            <p style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.6, textAlign: 'center', fontFamily: 'Manrope, sans-serif' }}>
              This is a live simulation. In reality, assessments are conducted through a secure, stress-free interface and reviewed by expert counselors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LiveAssessmentModal;
